/**
 * Claves de `localStorage` que pertenecen a UNA sesion de checkout.
 *
 * Todas describen "en que estaba el usuario mientras compraba": que pestania de
 * despacho tenia abierta, que direccion tecleo como invitado, que tienda de
 * retiro eligio, cuanto costaba el despacho antes del descuento. Ninguna es
 * necesaria una vez que la compra termino, y todas son ACTIVAS: el checkout las
 * lee al montar y se comporta segun lo que encuentre. Si sobreviven a un pedido
 * o a un cierre de sesion, el siguiente checkout arranca creyendo cosas de la
 * compra anterior — por ejemplo abriendo la pestania de retiro en tienda con
 * los datos de quien retira de otra persona.
 *
 * Trazabilidad de cada clave (quien escribe / quien lee):
 * - `sp_addr`: el medio aplicado es un retiro. Escriben ShippingMethodsForm,
 *   StorePickup, PickupPoint y los bridges de domicilio; leen ShippingMethodsForm,
 *   useCheckoutPageEffects y OrderSummary.
 * - `sp_address`: direccion de la tienda / punto de retiro elegido. Escribe
 *   ClickAndCollect; leen StorePickup y PickupPoint.
 * - `checkout_active_tab`: pestania de despacho abierta. Escriben
 *   ShippingMethodsForm, ShippingForm y los bridges; lee useCheckoutPageEffects.
 * - `hd_original_cost`: costo de despacho a domicilio antes del descuento, para
 *   el precio tachado. Escribe ShippingMethodsForm; leen los dos OrderSummary.
 * - `hd_selected_cost`: par del anterior. Hoy solo se escribe.
 * - `selected_hd_address`: ultima direccion de domicilio elegida. Escriben
 *   AddressTiles, CheckoutAddressForm y los bridges; lee ShippingForm.
 * - `guest_info`: direccion tecleada por el invitado. Escriben CheckoutAddressForm
 *   y los bridges; leen CheckoutAddressForm y ShippingForm.
 * - `pickup_customer_data`: nombre y RUT de quien retira. Escribe y lee
 *   ClickAndCollect.
 *
 * NO se incluye `order_info` A PROPOSITO: no es estado de la sesion de compra
 * sino el pedido YA CREADO, y es lo unico con lo que se pinta la pantalla de
 * resultado despues del pago. Lo escribe `usePlaceOrderFlow` justo antes de
 * mandar al usuario a la pasarela y lo consume `getInitialOrderInfo`
 * (`@lib/utils/checkoutUtils`), que ya lo borra el mismo cuando lo lee. Borrarlo
 * aqui dejaria al cliente que vuelve de la pasarela sin el numero de su pedido.
 *
 * Tampoco se incluye `cart_info`, que alimenta el evento de compra de analitica
 * en esa misma pantalla de resultado (`trackPurchaseEventFromCartInfo`), ni
 * `cart_id` / `pylot_token`, que son de la sesion y no del checkout.
 */
export const CHECKOUT_STORAGE_KEYS = [
    'sp_addr',
    'sp_address',
    'checkout_active_tab',
    'hd_original_cost',
    'hd_selected_cost',
    'selected_hd_address',
    'guest_info',
    'pickup_customer_data'
] as const

export type CheckoutStorageKey = (typeof CHECKOUT_STORAGE_KEYS)[number]

export type ClearCheckoutStorageOptions = {
    /**
     * Claves que NO se borran en esta llamada.
     *
     * Existe por un caso concreto y acotado: la limpieza al montar el checkout.
     * Ahi `guest_info` y `selected_hd_address` todavia se necesitan, porque son
     * la unica forma de recuperar la direccion de domicilio cuando el usuario
     * pasa a la pestania de retiro (que pisa la direccion del carro con la de
     * la tienda) y despues vuelve. Al terminar un pedido y al cerrar sesion no
     * se conserva nada.
     */
    keep?: readonly CheckoutStorageKey[]
}

/**
 * Borra el estado de checkout de `localStorage`.
 *
 * Se invoca en los tres momentos en los que ese estado deja de valer:
 * al terminar un pedido (invitado o registrado), al cerrar sesion y al montar
 * el checkout cuando no hay un retiro que restaurar.
 *
 * Nunca lanza: es limpieza, y no puede tumbar el camino del que la llama. En
 * Safari en modo privado `removeItem` puede lanzar, y ese fallo no debe abortar
 * el vaciado del carro despues de un cobro.
 */
export const clearCheckoutStorage = (
    options?: ClearCheckoutStorageOptions
): void => {
    if (typeof window === 'undefined') return

    const keep = options?.keep ?? []

    CHECKOUT_STORAGE_KEYS.forEach((key) => {
        if (keep.includes(key)) return

        try {
            window.localStorage.removeItem(key)
        } catch (error) {
            // Sin acceso a localStorage no hay nada que limpiar ni que reportar.
        }
    })
}
